import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useNotifications } from '../../contexts/NotificationsContext';
import type { Notification } from '@traaker/shared';

const SEVERITY_COLOR: Record<string, string> = {
  info: 'info.main',
  warning: 'warning.main',
  error: 'error.main',
};

function formatRelative(iso: string, t: TFunction): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return t('notificationsBell.justNow');
  if (mins < 60) return t('notificationsBell.minsAgo', { count: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t('notificationsBell.hoursAgo', { count: hours });
  const days = Math.floor(hours / 24);
  return t('notificationsBell.daysAgo', { count: days });
}

export default function NotificationsBell() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { items, unreadCount, markRead, markAllRead } = useNotifications();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const open = Boolean(anchor);
  const hasUnread = unreadCount > 0;

  const handleClickItem = async (notif: Notification) => {
    if (!notif.readAt) {
      await markRead(notif.notificationId);
    }
    setAnchor(null);
    if (notif.link) {
      navigate(notif.link);
    }
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{ color: hasUnread ? 'primary.main' : 'text.secondary' }}
      >
        <Badge badgeContent={unreadCount} color="error" max={9}>
          {hasUnread ? <NotificationsActiveIcon /> : <NotificationsNoneIcon />}
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: { width: 380, maxWidth: '90vw' } } }}
      >
        <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {t('notificationsBell.title')}
          </Typography>
          {hasUnread && (
            <Button size="small" onClick={markAllRead}>
              {t('notificationsBell.markAllRead')}
            </Button>
          )}
        </Box>
        <Divider />
        {items.length === 0 ? (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              {t('notificationsBell.empty')}
            </Typography>
          </MenuItem>
        ) : (
          items.slice(0, 10).map((n) => (
            <MenuItem
              key={n.notificationId}
              onClick={() => handleClickItem(n)}
              sx={{
                alignItems: 'flex-start',
                whiteSpace: 'normal',
                py: 1.25,
                bgcolor: n.readAt ? 'transparent' : 'action.hover',
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  mt: 0.75,
                  mr: 1.5,
                  bgcolor: SEVERITY_COLOR[n.severity] || 'primary.main',
                  flexShrink: 0,
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={n.readAt ? 400 : 600}>
                  {n.title}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {n.message}
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  {formatRelative(n.createdAt, t)}
                </Typography>
              </Box>
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );
}
